import { createContext, useContext, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "../lib/api.js";
import { Button } from "../components/ui/Button.js";
import { Card } from "@primereact/ui/card";
import { ProgressSpinner } from "@primereact/ui/progressspinner";
import { cx } from "../components/ui/cx.js";
import i18n from "../i18n/index.js";
import { usePermissions } from "../lib/auth.js";
export function LoadingPanel() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-48 items-center justify-center" role="status">
      <ProgressSpinner.Root aria-label={t("common.loading")}>
        <ProgressSpinner.Track />
        <ProgressSpinner.Range />
      </ProgressSpinner.Root>
      <span className="sr-only">{t("common.loading")}</span>
    </div>
  );
}
export function ErrorPanel({ error }: { error: unknown }) {
  const { t } = useTranslation();
  const message =
    error instanceof ApiError ? error.message : t("errors.generic");
  return (
    <Card.Root className="border border-danger-border bg-danger-soft p-5">
      <Card.Body>
        <Card.Content>
          <p role="alert" className="text-sm font-medium text-danger">
            {message}
          </p>
        </Card.Content>
      </Card.Body>
    </Card.Root>
  );
}
export function ExportCsvLink({
  dataset,
  query,
}: {
  dataset: "customers" | "requests" | "offers" | "projects" | "invoices";
  query?: Record<string, string | undefined>;
}) {
  const { t } = useTranslation();
  const { has } = usePermissions();
  if (!has("reports.export")) return null;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value) params.set(key, value);
  }
  const suffix = params.size ? `?${params.toString()}` : "";
  return (
    <Button
      as="a"
      href={`/api/v1/exports/${dataset}.csv${suffix}`}
      variant="outlined"
      severity="secondary"
    >
      {t("common.exportCsv", {
        defaultValue: "Export CSV",
      })}
    </Button>
  );
}
const FlatSectionsContext = createContext(false);

export function FlatSections({ children }: { children: ReactNode }) {
  return (
    <FlatSectionsContext.Provider value>
      {children}
    </FlatSectionsContext.Provider>
  );
}

export function Section({
  title,
  subtitle,
  actions,
  children,
  className,
  flat = false,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  flat?: boolean;
}) {
  const sectionsAreFlat = useContext(FlatSectionsContext);
  const content = (
    <>
      {title || actions ? (
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            {title ? (
              <h2 className="mb-1 text-base font-semibold text-app-text">
                {title}
              </h2>
            ) : null}
            {subtitle ? (
              <p className="text-sm text-app-muted">{subtitle}</p>
            ) : null}
          </div>
          {actions}
        </div>
      ) : null}
      {children}
    </>
  );
  if (flat || sectionsAreFlat)
    return <div className={cx("p-1 sm:p-2", className)}>{content}</div>;
  return (
    <Card.Root className={cx("p-4 sm:p-6", className)}>
      <Card.Body>
        <Card.Content>{content}</Card.Content>
      </Card.Body>
    </Card.Root>
  );
}
export function DetailGrid({ children }: { children: ReactNode }) {
  return (
    <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
      {children}
    </dl>
  );
}
export function Detail({
  label,
  value,
}: {
  label: ReactNode;
  value?: ReactNode;
}) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-app-muted">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-app-text">{value || "—"}</dd>
    </div>
  );
}
export function StickyActions({ children }: { children: ReactNode }) {
  return (
    <div className="sticky bottom-0 z-10 -mx-4 mt-6 flex flex-wrap justify-end gap-3 border-t border-app-border bg-app-surface/95 px-4 py-3 backdrop-blur sm:static sm:mx-0 sm:rounded-xl sm:border sm:px-5">
      {children}
    </div>
  );
}
export function MutationError({
  error,
  fieldLabels = {},
}: {
  error: unknown;
  fieldLabels?: Record<string, string>;
}) {
  const { t } = useTranslation();
  if (!error) return null;
  const fields =
    error instanceof ApiError && error.fields
      ? Object.entries(error.fields)
      : [];
  return (
    <div
      role="alert"
      className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger"
    >
      <p>{error instanceof Error ? error.message : t("errors.generic")}</p>
      {fields.length ? (
        <>
          <p className="mt-2 font-medium">
            {t("errors.invalidFields", {
              defaultValue: "Please correct these fields:",
            })}
          </p>
          <ul className="mt-1 list-disc space-y-1 pl-5">
            {fields.map(([field, message]) => (
              <li key={field}>
                <span className="font-medium">
                  {fieldLabels[field] ?? field}
                </span>
                {`: ${message}`}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}
export function ConfirmButton({
  children,
  confirmMessage,
  onConfirm,
  loading,
  variant = "secondary",
}: {
  children: ReactNode;
  confirmMessage: string;
  onConfirm: () => void;
  loading?: boolean;
  variant?: "secondary" | "danger";
}) {
  return (
    <Button
      variant="outlined"
      severity={variant === "danger" ? "danger" : "secondary"}
      loading={loading || undefined}
      onClick={() => window.confirm(confirmMessage) && onConfirm()}
    >
      {children}
    </Button>
  );
}
export function dateInputValue(date = new Date()): string {
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
/**
 * Format a date for display. Calendar dates ("YYYY-MM-DD") are shown as-is,
 * never shifted by the browser's time zone.
 */
export function formatDate(value?: string | null) {
  if (!value) return "—";
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T12:00:00`)
    : new Date(value);
  return new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language, {
    dateStyle: "medium",
  }).format(date);
}
export function formatMoney(value: string | number, currency = "CHF") {
  return new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language, {
    style: "currency",
    currency,
  }).format(Number(value));
}
